<template>

  <v-container fluid class="register-org-form">

    <!-- Title and close action -->
    <v-row class="mt-1">
      <v-col cols="12" xs="12" sm="9" md="9" lg="9">
        <div style="font-size:1.5rem;">
          {{ titleText }}
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="3" md="3" lg="3" align="right">
        <a class="pr-4" style="font-size:16px;" @click="close" v-if="!isProcessing">Close</a>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container v-if="isProcessing" class="mt-10 d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate :size="40" style="margin-bottom:15px" color="primary" />
      <div style="font-size:1.14rem;">Registering org</div>
    </v-container>

    <!----------------------->
    <!-- Register Org Form -->
    <!----------------------->
    <v-container fluid class="mt-8 page-content" v-else>
      <!-- info text -->
      <v-row align="center" class="info-container">
        <v-col cols="2" xs="2" sm="2" md="1" lg="1" align="center">
          <v-icon style="font-size:1.6rem;" color="yellow darken-3">mdi-information-outline</v-icon>
        </v-col>
        <v-col cols="10" xs="8" sm="9" md="10" lg="9" align="left">
          <div>A registered org can only be claimed by the owner specified.</div>
          <!-- <div class="pt-1">
            Once claimed, the owner can invite other members to the org.
          </div> -->
        </v-col>
      </v-row>

      <!-- org name -->
      <v-row align="center" class="mt-8">
        <v-col cols="12" xs="12" sm="2" md="2" lg="2">
          <span class="attr-label">Org name:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="8" md="7" lg="7">
          <v-text-field
              outlined
              hide-details
              v-model.trim="orgName" />
        </v-col>
      </v-row>

      <!-- owner email -->
      <v-row align="center" class="mt-8">
        <v-col cols="12" xs="12" sm="2" md="2" lg="2">
          <span class="attr-label">Owner email:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="8" md="7" lg="7">
          <v-text-field
              outlined
              hide-details
              v-model.trim="ownerEmail" />
        </v-col>
      </v-row>

      <!-- send email option -->
      <v-row align="center" class="mt-8">
        <v-col cols="12">
          <v-checkbox
              hide-details
              label="Send notification email"
              v-model="sendEmailOption">
            <template v-slot:label>
              <div class="send-email-label-text">Send notification email</div>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>
      <v-row align="center" class="pl-1 mt-1">
        <v-col cols="12" class="attr-info-text">
          <div>If selected, an email will be sent to the owner with a claim link.</div>
          <div>If they do not have a {{ appName }} account, they will be guided to signup.</div>
        </v-col>
      </v-row>
    </v-container>

    <!------------------------------>
    <!-- Action and Message Area -->
    <!------------------------------>
    <v-container style="text-align:center;">
      <!-- success message, register another button -->
      <template v-if="registerSuccessMessage">
        <transition name="fade" appear>
          <v-row class="pt-6" align="center">
            <v-col>
              <div class="success-message-text">
                <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
                <span>{{ registerSuccessMessage }}</span>
              </div>
            </v-col>
            <v-col>
              <v-btn text color="light-blue darken-1" @click="resetForm">
                Register Another
              </v-btn>
            </v-col>
          </v-row>
        </transition>
      </template>

      <!-- error message -->
      <template v-else-if="registerError">
        <transition name="fade" appear>
          <v-row class="pt-8" align="center">
            <v-col cols="9" class="error-message-text">
              <v-icon color="red darken-2" class="mr-2 pb-1">mdi-alert-box-outline</v-icon>
              <span>{{ registerError }}</span>
            </v-col>
            <v-col cols="3">
              <v-btn text color="light-blue darken-1" @click="resetForm">
                Clear Form
              </v-btn>
            </v-col>
          </v-row>
        </transition>
      </template>

      <!-- register org button -->
      <v-row align="center" v-else>
        <v-col class="mt-4">
          <v-btn
              :disabled="!isReadyToRegister || isProcessing"
              class="action-button"
              color="blue"
              @click="registerOrg">
            Register Org
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

  </v-container>

</template>

<script>
export default {
  name: 'RegisterOrgForm',

  props: {
    org_type_code: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      isProcessing: false,
      registerError: null,
      orgName: null,
      ownerEmail: null,
      sendEmailOption: false,
      registeredOrg: null,
      registerSuccessMessage: null,
      registeredCount: 0
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    titleText () {
      switch (this.org_type_code) {
        case 'shipper': return 'Register Shipper Org'
        case 'receiver': return 'Register Receiver Org'
        default: return 'Register Org'
      }
    },
    isReadyToRegister () {
      return this.orgName && this.ownerEmail
    }
  },

  beforeDestroy () {
    this.resetForm()
  },

  methods: {
    // Emit close action
    close () {
      this.$emit('close', { registered: this.registeredCount })
      this.resetForm()
      this.registeredCount = 0
    },

    // Reset form data
    resetForm () {
      this.registerError = null
      this.orgName = null
      this.ownerEmail = null
      this.sendEmailOption = false
      this.registeredOrg = null
      this.registerSuccessMessage = null
    },

    // Perform register org
    async registerOrg () {
      if (!this.isReadyToRegister) return false
      this.isProcessing = true

      try {
        let uri = ''
        switch (this.org_type_code) {
          case 'shipper': uri = 'shipper-org/register'; break
          case 'receiver': uri = 'receiver-org/register'; break
        }

        // Register org
        this.registeredOrg = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri,
          opts: {
            bodyData: {
              org_name: this.orgName,
              owner_email: this.ownerEmail,
              send_email: this.sendEmailOption
            }
          }
        })

        // Increment invited count
        ++this.registeredCount

        // Show success message
        this.registerSuccessMessage = 'Org Registered'
      } catch (err) {
        this.registerError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style>
  .register-org-form .info-container {
    font-size: 1.1rem;
    color: #F9A825;
  }

  .register-org-form .attr-label {
    color: #9c9c9c;
    font-size: 1.0rem;
    font-weight: 400;
  }

  .register-org-form .send-email-label-text {
    color: #9c9c9c;
    font-size: 1.15rem;
    font-weight: 400;
  }

  .register-org-form .attr-info-text {
    color: #727272;
    font-size: 1.0rem;
    font-weight: 400;
  }

  .register-org-form .action-button {
    height: 52px !important;
    width: 140px;
  }

  .register-org-form .success-message-text {
    font-size: 1.23em;
    color: #4CAF50;
    font-weight: 400;
  }

  .register-org-form .error-message-text {
    font-size: 1.15em;
    color: #D32F2F;
    font-weight: 400;
  }
</style>
